import { useState } from "react";
import { useAdminBlogs } from "@/hooks/useBlogs";
import { deleteBlog } from "@/lib/api";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { AdminPanelHeader } from "./AdminPanelHeader";

type Props = {
  onEdit: (blogId: string) => void;
  onNew: () => void;
};

export function BlogList({ onEdit, onNew }: Props) {
  const { data: blogs, loading, refetch } = useAdminBlogs();
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeleting(id);
    await deleteBlog(id);
    setDeleting(null);
    refetch();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-dark border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <AdminPanelHeader
          title="Blog Posts"
          action={
            <button
              type="button"
              onClick={onNew}
              className="inline-flex items-center gap-2 rounded-xl bg-pink-dark px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-px active:scale-95"
            >
              <Plus className="h-4 w-4" />
              New Post
            </button>
          }
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(blogs ?? []).map((blog) => (
              <tr key={blog.id} className="hover:bg-gray-50">
                <td className="max-w-[20rem] truncate px-4 py-3 font-medium">
                  {blog.title}
                </td>
                <td className="px-4 py-3 text-gray-600">{blog.category}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                      blog.is_published
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {blog.is_published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                  {blog.published_at ?? "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(blog.id)}
                      className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-pink-dark"
                      aria-label="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(blog.id, blog.title)}
                      disabled={deleting === blog.id}
                      className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {(!blogs || blogs.length === 0) && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                  No blog posts yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
